import type { LayersOutput } from '@festina/configuration-general-types/configuration'
import {
  type ConfigurableServer,
  notEmpty,
  type Optional,
} from '@festina-life/core'
import { useSuspenseQuery } from '@tanstack/react-query'

import { isConfigurationServerService } from '../../../hooks/helpers'
import { type ServerServicesType, useService } from '../../../hooks/useService'
import { fetchLayersKey } from '../keys'
import { transformPairsToTree } from './helpers'

export const fetchLayers = async ({
  service,
  server,
}: {
  server: Optional<ConfigurableServer>
  service: Optional<ServerServicesType>
}): Promise<Optional<LayersOutput>> => {
  if (!service || !server) {
    return Promise.resolve(null)
  }

  try {
    return await service.api.ConfigurationMetaService.metaServiceGetGetLayers({
      componentName: server.toLocaleLowerCase(),
    })
  } catch (e) {
    if (e.status >= 400 && e.status < 500) {
      return {
        _type: 'LayersOutput',
        layers: [],
        isStrictlyMoreGeneralPairs: [],
      } satisfies LayersOutput
    } else {
      throw e
    }
  }
}

export const useFetchLayers = (server: Optional<ConfigurableServer>) => {
  const service = useService(server)
  const isEnabled = !!server && !!service

  return useSuspenseQuery({
    queryKey: isEnabled
      ? fetchLayersKey({
          server,
          isFromConfigurationServer: service
            ? isConfigurationServerService(service)
            : false,
        })
      : [],
    queryFn: () => fetchLayers({ service, server }),
    staleTime: 'static',
    select: (data) =>
      transformPairsToTree({
        items: data?.layers?.filter(notEmpty),
        strictlyMoreGeneralPairs:
          data?.isStrictlyMoreGeneralPairs?.filter(notEmpty),
      }),
  })
}
